import * as React from "react";
import {
  Box,
  Button,
  Grid,
  InputLabel,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { ImagesContext } from "../Context";
import { getHeaders } from "../utils/HeaderHelper";
import { MAX_PASSWORD_LENGTH, getPasswordError } from "../utils/DemographicValidationHelper";
import { useFlash } from "../FlashContext";

interface Props {
  expanded: boolean;
  setExpanded: any;
  csrfToken: string;
}

const ChangePassword: React.FC<Props> = ({
  expanded,
  setExpanded,
  csrfToken,
}) => {
  const images = React.useContext(ImagesContext);
  const { showToast } = useFlash();

  const [currentPassword, setCurrentPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [newPasswordVerification, setNewPasswordVerification] =
    React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);

  const handleExpand = () => {
    setExpanded(true);
  };

  const handleCancel = () => {
    setExpanded(false);
    setCurrentPassword("");
    setNewPassword("");
    setNewPasswordVerification("");
  };

  const validatePassword = (password: string) => {
    if (password.length < 6) return false;
    const hasLetter = /[a-zA-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecial = /[^a-zA-Z0-9]/.test(password);
    return hasLetter && hasNumber && hasSpecial;
  };

  const handlePasswordChange = async () => {
    if (!currentPassword) {
      showToast({
        title: "Password Required",
        text: "Current password is required",
        type: "error",
      });
      return;
    }

    if (!newPassword) {
      showToast({
        title: "New Password Required", 
        text: "New password is required",
        type: "error",
      });
      return;
    }

    if (newPassword === currentPassword) {
      showToast({
        title: " Password Invalid",
        text: "New password cannot be the same as the current password",
        type: "error",
      });
      return;
    }

    if (!validatePassword(newPassword)) {
      showToast({
        title: "Password Invalid",
        text: "Password must be at least 6 characters and include numbers, letters, and special characters e.g. (!$@%#)",
        type: "error",
      });
      return;
    }

    if (newPassword !== newPasswordVerification) {
      showToast({
        title: "Password Invalid",
        text: "New password confirmation doesn't match",
        type: "error",
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch("/change_password", {
        method: "POST",
        headers: {
          ...getHeaders(csrfToken),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
          new_password_confirmation: newPasswordVerification,
        }),
      });

      const result = await response.json();

      if (result.success) {
        showToast({
          title: "Success",
          text: result.message,
          type: "success",
        });
        handleCancel();
      } else {
        showToast({
          title: "Error", 
          text: result.message,
          type: "error",
        });
      }
    } catch (error) {
      showToast({
        title: "Password Error",
        text: "An error occurred while updating your password",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "4px",
      }}
    >
      <Box
        className="profile-list-tile"
        sx={{
          boxShadow: expanded && "0px 7px 7px 0px #00000040",
        }}
      >
        <Box
          sx={{
            display: "flex",
            columnGap: "16px",
            alignItems: "center",
          }}
        >
          <div className="item-icon-container">
            <img
              src={images.password_change_icon}
              alt="Insurance Icon"
              className="action-icon-image"
              style={{ width: 23, maxHeight: 23 }}
            />
          </div>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              rowGap: "10px",
            }}
          >
            <Box display="flex" alignItems="center" columnGap="16px">
              <Typography
                sx={{
                  fontSize: "18px",
                  lineHeight: "18px",
                  fontWeight: 700,
                  letterSpacing: "1px",
                  color: "#4A4442",
                }}
              >
                Password
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: "14px",
                lineHeight: "18px",
                fontWeight: 500,
                letterSpacing: "1px",
                color: "#707070",
              }}
            >
              Create a new password for your account
            </Typography>
          </Box>
        </Box>

        {expanded ? (
          <Box className="row">
            <Link
              style={{
                marginRight: "16px",
                marginLeft: "32px",
                color: "black",
              }}
              onClick={handleCancel}
            >
              Cancel
            </Link>
            <Link className="save-btn" onClick={handlePasswordChange}>
              Save
            </Link>
          </Box>
        ) : (
          <Button className="expand-button" onClick={handleExpand}>
            Update
          </Button>
        )}
      </Box>

      {expanded && (
        <Box className="edit-container">
          <Grid item xs={12} className="profile-edit-container">
            <Grid container>
              <Grid
                container
                className="profile-form-container"
                columnSpacing={3}
              >
                <Grid item xs={3} sx={{ marginTop: "24px" }}>
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography
                        sx={{
                          color: "#A29D9B",
                          fontSize: "12px",
                          lineHeight: "18px",
                          fontWeight: 700,
                        }}
                      >
                        Your password must be at least 6 characters and should
                        include a combination of numbers, letters, and special
                        characters e.g. (!$@%#)
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={2.5}>
                  <Grid container>
                    <Grid item xs={12}>
                      <InputLabel
                        htmlFor="current_password"
                        className="field-label"
                      >
                        Current Password*
                      </InputLabel>
                    </Grid>
                    <Grid item xs={12} className="field-container">
                      <TextField
                        size="small"
                        value={currentPassword}
                        className="the-field"
                        required
                        variant="outlined"
                        placeholder="Current Password"
                        autoComplete="off"
                        type="password"
                        inputProps={{
                          maxLength: MAX_PASSWORD_LENGTH
                        }}
                        error={Boolean(getPasswordError(currentPassword, true))}
                        helperText={getPasswordError(currentPassword, true)}
                        onChange={(event) => {
                          const raw = event.target.value;
                          const trimmedValue = raw.replace(/^\s+/, '');
                          if (trimmedValue.length > MAX_PASSWORD_LENGTH) return;
                          setCurrentPassword(trimmedValue);
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={2.5}>
                  <Grid container>
                    <Grid item xs={12}>
                      <InputLabel
                        htmlFor="new_password"
                        className="field-label"
                      >
                        New Password*
                      </InputLabel>
                    </Grid>
                    <Grid item xs={12} className="field-container">
                      <TextField
                        size="small"
                        value={newPassword}
                        required
                        variant="outlined"
                        className="the-field"
                        placeholder="New Password"
                        autoComplete="off"
                        type="password"
                        inputProps={{
                          maxLength: MAX_PASSWORD_LENGTH
                        }}
                        error={Boolean(getPasswordError(newPassword, true))}
                        helperText={getPasswordError(newPassword, true)}
                        onChange={(event) => {
                          const raw = event.target.value;
                          const trimmedValue = raw.replace(/^\s+/, '');
                          if (trimmedValue.length > MAX_PASSWORD_LENGTH) return;
                          setNewPassword(trimmedValue);
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={2.5}>
                  <Grid container>
                    <Grid item xs={12}>
                      <InputLabel
                        htmlFor="verify_new_password"
                        className="field-label"
                      >
                        Verify New Password*
                      </InputLabel>
                    </Grid>
                    <Grid item xs={12} className="field-container">
                      <TextField
                        size="small"
                        value={newPasswordVerification}
                        required
                        variant="outlined"
                        className="the-field"
                        placeholder="New Password Confirmation"
                        autoComplete="off"
                        type="password"
                        inputProps={{
                          maxLength: MAX_PASSWORD_LENGTH
                        }}
                        error={Boolean(getPasswordError(newPasswordVerification, true))}
                        helperText={getPasswordError(newPasswordVerification, true)}
                        onChange={(event) => {
                          const raw = event.target.value;
                          const trimmedValue = raw.replace(/^\s+/, '');
                          if (trimmedValue.length > MAX_PASSWORD_LENGTH) return;
                          setNewPasswordVerification(trimmedValue);
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default ChangePassword;
